/**
 * The legend's grouping. One rule throughout: a heading names what its rows share, a row
 * names what tells it apart, and nothing repeats what the card's title or the panel already says.
 *
 * - A panel with several categories heads its rows with the category. A category with a single row
 *   folds its heading into the row, which then reads "Precipitation · Vue ISS".
 * - A panel with one category needs no category heading. It heads its rows with zones only when
 *   they come from more than one: "This zone", then each other zone. Not a third level under categories.
 * - A row is the device and its sensor. On a device card the title names the device, so the row is the sensor.
 * - Where the stream sits goes after the row's name: "in 3 zones", counting the card's, for the card's
 *   own stream that other zones share, the zone's name for one added from elsewhere.
 */
import type { CardRelation } from "../../ChartSpec";
import type { PreparedSeries } from "../prepare-series";
import type { ReadoutRow } from "./readout";

export type LineLocation = { kind: "shared" | "away"; zoneNames: string[] };

export type LegendLine = {
    row: ReadoutRow;
    label: string;
    /** Joined with " · ". Empty when the label says it all. */
    detail: string;
    location: LineLocation | null;
};

export type LegendHeading =
    | { kind: "category"; name: string; axisUnitCategory: string }
    /** `own` is the card's zone. A null name on another zone is a stream that sits in none. */
    | { kind: "zone"; own: boolean; name: string | null };

export type LegendGroup = { key: string; heading: LegendHeading | null; lines: LegendLine[] };

const OWN_RELATION: CardRelation = { own: true, otherZoneNames: [], hostNamedByCard: false };

function relationOf(row: ReadoutRow): CardRelation {
    return row.series.cardRelation ?? OWN_RELATION;
}

/** Rows by key, in the order each key first appears. */
function partition(rows: ReadoutRow[], keyOf: (row: ReadoutRow) => string): Map<string, ReadoutRow[]> {
    const groups = new Map<string, ReadoutRow[]>();
    rows.forEach((row) => {
        const key = keyOf(row);
        const group = groups.get(key);
        if (group) group.push(row);
        else groups.set(key, [row]);
    });
    return groups;
}

/** The sensor cluster, else a channel name that isn't just the metric again. Empty when neither says anything. */
function sensorNameOf(series: PreparedSeries, metricName: string): string {
    return (
        series.sensorClusterName ??
        (series.sensorElementName && series.sensorElementName !== metricName ? series.sensorElementName : "")
    );
}

function toLine(row: ReadoutRow, metricName: (unitCategory: string) => string): LegendLine {
    const { series } = row;
    const relation = relationOf(row);
    const metric = metricName(series.unitCategory);
    const sensor = sensorNameOf(series, metric);

    let location: LineLocation | null = null;
    if (!relation.own) location = { kind: "away", zoneNames: relation.otherZoneNames };
    else if (relation.otherZoneNames.length > 0) {
        const cardZones = relation.cardZoneName ? [relation.cardZoneName] : [];
        location = { kind: "shared", zoneNames: [...cardZones, ...relation.otherZoneNames] };
    }

    return relation.hostNamedByCard
        ? { row, label: sensor || metric, detail: "", location }
        : { row, label: series.name, detail: sensor, location };
}

/** The category's name becomes the label, and what the label said moves into the detail. */
function foldIntoCategory(line: LegendLine, metric: string): LegendLine {
    const detail = [line.label, line.detail].filter((part) => part !== "" && part !== metric).join(" · ");
    return { ...line, label: metric, detail };
}

function groupByCategory(rows: ReadoutRow[], metricName: (unitCategory: string) => string): LegendGroup[] {
    return [...partition(rows, (row) => row.series.unitCategory)].map(([category, categoryRows]) => {
        const lines = categoryRows.map((row) => toLine(row, metricName));
        const key = `category:${category}`;
        const metric = metricName(category);

        if (lines.length === 1) return { key, heading: null, lines: [foldIntoCategory(lines[0], metric)] };

        return {
            key,
            heading: { kind: "category", name: metric, axisUnitCategory: categoryRows[0].series.axisUnitCategory },
            lines,
        };
    });
}

type ZoneOf = { key: string; own: boolean; name: string | null };

/** A stream in several zones goes under its first, as the stream picker does. */
function zoneOf(row: ReadoutRow): ZoneOf {
    const relation = relationOf(row);
    if (relation.own) return { key: "own", own: true, name: null };
    const name = relation.otherZoneNames[0] ?? null;
    return { key: name === null ? "none" : `zone:${name}`, own: false, name };
}

function groupByZone(rows: ReadoutRow[], metricName: (unitCategory: string) => string): LegendGroup[] {
    const byZone = partition(rows, (row) => zoneOf(row).key);
    if (byZone.size <= 1) return [{ key: "all", heading: null, lines: rows.map((row) => toLine(row, metricName)) }];

    const groups = [...byZone.values()].map((zoneRows) => {
        const { key, own, name } = zoneOf(zoneRows[0]);
        const lines = zoneRows.map((row) => {
            const line = toLine(row, metricName);
            if (own) return line;
            // The heading names the stream's first zone, so only a stream in more than one needs its own location.
            const { otherZoneNames } = relationOf(row);
            const location: LineLocation | null =
                otherZoneNames.length > 1 ? { kind: "shared", zoneNames: otherZoneNames } : null;
            return { ...line, location };
        });
        return { key, heading: { kind: "zone" as const, own, name }, lines };
    });
    // The card's own zone first, the others in the order their streams arrived.
    return [...groups.filter((group) => group.heading.own), ...groups.filter((group) => !group.heading.own)];
}

export function groupLegend(rows: ReadoutRow[], metricName: (unitCategory: string) => string): LegendGroup[] {
    const categoryCount = new Set(rows.map((row) => row.series.unitCategory)).size;
    return categoryCount > 1 ? groupByCategory(rows, metricName) : groupByZone(rows, metricName);
}
